import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { FrontendType } from '../enums/frontend-type.enum';
import { HttpCustomHeader } from '../enums/http-custom-header.enum';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { LogFactory } from '../services/logger/log-factory';
import { PlatformService } from '../services/platform/platform.service';
import { StringService } from '../utils/string/string.service';

// The API path the admin session cookie is scoped to (ADR-0122).
const ADMIN_SESSION_COOKIE_PATH = '/authentication/';

// The only routes the API honours a half-signed-in (MFA-pending) token on: see
// MFARoutes in the API's AccessControlGuard and ADR-0302.
const MFA_PENDING_TOKEN_URLS = [
  '/authentication/mfa/verify',
  '/authentication/mfa/request-reset',
  '/authentication/mfa/initiate-registration',
  '/authentication/mfa/confirm-registration',
];

@Injectable({
  providedIn: 'root',
})
export class AuthenticationInterceptorService implements HttpInterceptor {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly stringService = inject(StringService);
  private readonly platformService = inject(PlatformService);

  private readonly logger = inject(LogFactory).createLogger(
    AuthenticationInterceptorService,
  );

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Add headers for all requests
    const authReq = this.addAuthHeaders(
      req,
      this.tokenFor(req.url),
      this.platformService.getBaseHref(),
    );

    // Send the request and handle errors
    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        // If a 403 occurs for authentication URLs, it means the token or credentials are invalid.
        // The safest action is to log out to clear local storage.
        if (err.status === 403 && this.isAuthUrl(req.url)) {
          this.logger.warn(
            'Caught 403 on auth URL; token/credentials invalid, logging out',
            { url: req.url },
          );
          this.authenticationService.logout().subscribe();
        }

        return throwError(() => err);
      }),
    );
  }

  private addAuthHeaders(
    req: HttpRequest<unknown>,
    token: string | null,
    frontendType: FrontendType,
  ): HttpRequest<unknown> {
    let headers = req.headers.set(HttpCustomHeader.FRONTEND_TYPE, frontendType);

    if (token) {
      const bearerToken = this.stringService.getBearerToken(token);
      if (bearerToken) {
        headers = headers.set(
          HttpCustomHeader.ACCESS_TOKEN_HEADER,
          bearerToken,
        );
      }
    }

    return req.clone({
      headers,
      // Admin: the session's refresh token is an HttpOnly cookie on the API's
      // /authentication path (ADR-0122). Cross-origin, the browser only stores
      // and sends it on requests made with credentials.
      ...(this.platformService.isAdminUi() &&
      req.url.includes(ADMIN_SESSION_COOKIE_PATH)
        ? { withCredentials: true }
        : {}),
    });
  }

  /**
   * The access token to send with this request, or null to send it as a guest.
   *
   * An admin who has passed the password step but still owes MFA (or forced
   * MFA set-up) holds a token the API honours on the MFA routes ONLY; an API
   * before ADR-0302 refused it with 403 everywhere else, public routes
   * included. Until MFA completes the caller is a guest, so it is sent as one.
   */
  private tokenFor(url: string): string | null {
    if (this.isAuthUrl(url)) return null;
    if (
      this.authenticationService.hasMfaPendingSession() &&
      !this.isMfaPendingTokenUrl(url)
    ) {
      return null;
    }
    return this.authenticationService.getAccessToken();
  }

  private isMfaPendingTokenUrl(url: string): boolean {
    const path = url.split('?')[0];
    return MFA_PENDING_TOKEN_URLS.some((mfaUrl) => path.endsWith(mfaUrl));
  }

  private isAuthUrl(url: string): boolean {
    // Public auth endpoints where we must NOT forward the stored access
    // token. If we did, a stale token (e.g. one with mfaRequired=true left
    // over from an abandoned MFA flow) would be sent on the new login
    // attempt, and the backend's AccessControlGuard would reject the
    // re-login with 403 because mfaRequired blocks every route except
    // /authentication/mfa/verify.
    return (
      url.endsWith('/authentication/login') ||
      url.endsWith('/authentication/admin/login') ||
      url.endsWith('/authentication/refresh-token') ||
      url.endsWith('/authentication/request-password-reset') ||
      url.endsWith('/authentication/reset-password') ||
      url.endsWith('/authentication/register') ||
      url.endsWith('/authentication/register-organization') ||
      url.endsWith('/authentication/verify-email') ||
      url.endsWith('/authentication/oauth') ||
      url.endsWith('/authentication/oauth/bridge-exchange') ||
      url.endsWith('/authentication/oauth/nonce') ||
      url.endsWith('/authentication/request-email-verification') ||
      url.endsWith('/authentication/verify-email-change') ||
      url.endsWith('/authentication/mfa/disable-with-token') ||
      url.endsWith('/authentication/logout')
    );
  }
}

export const authenticationInterceptor: HttpInterceptorFn = (req, next) => {
  const interceptor = inject(AuthenticationInterceptorService);
  const nextHandler: HttpHandler = {
    handle: (request: HttpRequest<any>) => next(request),
  };
  return interceptor.intercept(req, nextHandler);
};
